import { FC, CSSProperties } from 'react';
import { theme } from '../../theme';
import { skeletonStyles } from './styles';

export interface SkeletonProps {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  style?: CSSProperties;
  testID?: string;
}

/** Bloque gris pulsante que reserva el espacio de un contenido mientras carga. */
export const Skeleton: FC<SkeletonProps> = ({ width = '100%', height = 16, radius, style, testID }) => (
  <span
    aria-hidden="true"
    data-ui="skeleton-bone"
    data-testid={testID}
    style={{
      ...skeletonStyles.bone,
      width,
      height,
      ...(radius !== undefined && { borderRadius: radius }),
      ...style,
    }}
  />
);

export interface InstallmentSkeletonListProps {
  /** Cantidad de filas que se muestran mientras carga (por defecto 4, el número típico de planes). */
  rows?: number;
  testID?: string;
}

/** Filas con la misma forma que `InstallmentOption`: radio + nombre + total + detalle. */
export const InstallmentSkeletonList: FC<InstallmentSkeletonListProps> = ({
  rows = 4,
  testID = 'installment-skeleton',
}) => (
  <div style={skeletonStyles.installmentList} data-testid={testID}>
    {Array.from({ length: rows }, (_, index) => (
      <div key={index} style={skeletonStyles.installmentRow}>
        <Skeleton width={theme.spacing.mediumLarge} height={theme.spacing.mediumLarge} radius="50%" />
        <div style={skeletonStyles.installmentTexts}>
          <Skeleton width="55%" height={14} />
          <Skeleton width="30%" height={10} />
        </div>
        <div style={{ ...skeletonStyles.installmentTexts, alignItems: 'flex-end', flex: 'none' }}>
          <Skeleton width={80} height={14} />
          <Skeleton width={60} height={10} />
        </div>
      </div>
    ))}
  </div>
);

export interface SpinnerProps {
  size?: number;
  color?: string;
  /** Texto accesible; si se omite, el spinner se marca como decorativo. */
  label?: string;
  testID?: string;
}

/** Indicador circular para acciones en curso (botones, envíos). */
export const Spinner: FC<SpinnerProps> = ({
  size = 16,
  color = theme.colors.white,
  label,
  testID = 'spinner',
}) => (
  <span
    role={label ? 'status' : undefined}
    aria-label={label}
    aria-hidden={label ? undefined : true}
    data-ui="spinner"
    data-testid={testID}
    style={{
      ...skeletonStyles.spinner,
      width: size,
      height: size,
      borderWidth: Math.max(2, Math.round(size / 8)),
      borderColor: `${color} transparent ${color} transparent`,
    }}
  />
);
